import axios from 'axios';
import { 
  User, 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  PasswordResetRequest, 
  PasswordResetConfirm,
  ChangePassword,
  UpdateDefaultOrganization 
} from '../types/auth.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with interceptors
const authApi = axios.create({
  baseURL: `${API_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh for login, register, or refresh endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/login/') || 
                          originalRequest.url?.includes('/register/') || 
                          originalRequest.url?.includes('/refresh/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await authApi.post('/refresh/', {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;
          localStorage.setItem('accessToken', access);
          localStorage.setItem('refreshToken', refresh);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return authApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await authApi.post<AuthResponse>('/login/', credentials);
    const { access, refresh } = response.data;
    
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    
    return response.data;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const { passwordConfirm, ...registerData } = data;
    const response = await authApi.post<AuthResponse>('/register/', {
      ...registerData,
      password_confirm: passwordConfirm,
      first_name: data.firstName,
      last_name: data.lastName,
      phone_number: data.phoneNumber,
    });
    
    const { access, refresh } = response.data;
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    
    return response.data;
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    
    try {
      if (refreshToken) {
        await authApi.post('/logout/', { refresh: refreshToken });
      }
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await authApi.get('/profile/');
    const user = response.data;
    // Transform snake_case to camelCase
    return {
      id: user.id,
      email: user.email,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      phoneNumber: user.phone_number,
      avatar: user.avatar,
      isActive: user.is_active,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      defaultCompany: user.default_company,
      defaultCompanyId: user.default_company_id,
      defaultCompanyName: user.default_company_name,
      defaultTeam: user.default_team,
      defaultTeamId: user.default_team_id,
      defaultTeamName: user.default_team_name,
      defaultDepartment: user.default_department,
      defaultDepartmentId: user.default_department_id,
      defaultDepartmentName: user.default_department_name,
    };
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await authApi.patch<User>('/profile/', {
      first_name: data.firstName,
      last_name: data.lastName,
      phone_number: data.phoneNumber,
    });
    return response.data;
  }

  async changePassword(data: ChangePassword): Promise<void> {
    await authApi.post('/change-password/', {
      old_password: data.oldPassword,
      new_password: data.newPassword,
      new_password_confirm: data.newPasswordConfirm,
    });
  }

  async updateDefaultOrganization(data: UpdateDefaultOrganization): Promise<User> {
    const requestData: any = {
      default_company: data.defaultCompany,
      default_team: data.defaultTeam,
      default_department: data.defaultDepartment,
    };
    
    // Add user_id if provided (for admin updating other users)
    if (data.userId) {
      requestData.user_id = data.userId;
    }
    
    const response = await authApi.patch('/update-default-organization/', requestData);
    
    const user = response.data;
    return {
      id: user.id,
      email: user.email,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      phoneNumber: user.phone_number,
      avatar: user.avatar,
      isActive: user.is_active,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      defaultCompany: user.default_company,
      defaultCompanyId: user.default_company_id,
      defaultCompanyName: user.default_company_name,
      defaultTeam: user.default_team,
      defaultTeamId: user.default_team_id,
      defaultTeamName: user.default_team_name,
      defaultDepartment: user.default_department,
      defaultDepartmentId: user.default_department_id,
      defaultDepartmentName: user.default_department_name,
    };
  }

  async requestPasswordReset(data: PasswordResetRequest): Promise<void> {
    await authApi.post('/password-reset/', data);
  }

  async confirmPasswordReset(data: PasswordResetConfirm): Promise<void> {
    await authApi.post('/password-reset-confirm/', {
      token: data.token,
      password: data.password,
      password_confirm: data.passwordConfirm,
    });
  }

  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    const response = await authApi.get('/verify/');
    return response.data;
  }

  async getUsers(filter?: { company?: string; assignableToRisk?: string; isActive?: boolean }): Promise<User[]> {
    // page_size lifts the 20-row default so an org's full membership fits in
    // one page, and the loop covers a membership larger than the server's
    // MAX_PAGE_SIZE. This used to send page_size alone, which the stock
    // pagination class ignored, so the owner and assignee dropdowns quietly
    // offered the first 20 people in the organization.
    const params: Record<string, string | number> = { page_size: 500 };
    if (filter?.company) params.company = filter.company;
    if (filter?.assignableToRisk) params.assignable_to_risk = filter.assignableToRisk;
    // Pickers offer people who can act on the work, so a deactivated account
    // is never a candidate. BooleanWidget on the backend reads true/false.
    if (filter?.isActive !== undefined) params.is_active = String(filter.isActive);

    const rows: any[] = [];
    let page = 1;
    for (;;) {
      const response = await authApi.get('/users/', { params: { ...params, page } });
      const body = response.data;
      if (!body?.results) {
        rows.push(...(Array.isArray(body) ? body : []));
        break;
      }
      rows.push(...body.results);
      if (!body.next) break;
      page += 1;
    }
    const data = rows;
    
    // Transform snake_case to camelCase
    return data.map((user: any) => ({
      id: user.id,
      email: user.email,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      phoneNumber: user.phone_number,
      avatar: user.avatar,
      isActive: user.is_active,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      defaultCompany: user.default_company,
      defaultCompanyId: user.default_company_id,
      defaultCompanyName: user.default_company_name,
      defaultTeam: user.default_team,
      defaultTeamId: user.default_team_id,
      defaultTeamName: user.default_team_name,
      defaultDepartment: user.default_department,
      defaultDepartmentId: user.default_department_id,
      defaultDepartmentName: user.default_department_name,
    }));
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }
}

const authService = new AuthService();
export default authService;